import React, { useState, useRef, useEffect } from "react";
import { Play, Radio, Volume2 } from "lucide-react";
import Sigil from "./Sigil";

const VIDEO_PLAYLIST = [
  "/LTX_2.3_i2v_00155_.mp4",
  "/LTX_2.3_i2v_00159_.mp4",
  "/LTX_2.3_i2v_00160_.mp4",
  "/LTX_2.3_i2v_00161_.mp4",
  "/LTX_2.3_i2v_00162_.mp4",
  "/LTX_2.3_i2v_00163_.mp4",
  "/LTX_2.3_i2v_00164_.mp4",
];

const VideoSection = () => {
  const [playing, setPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    if (playing && videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [currentIndex, playing]);

  const handleEnded = () => {
    if (currentIndex < VIDEO_PLAYLIST.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setPlaying(false);
      setCurrentIndex(0);
    }
  };

  const handlePlay = () => {
    setCurrentIndex(0);
    setPlaying(true);
  };

  return (
    <section
      id="transmision"
      data-testid="video-section"
      className="relative bg-[#050505] py-24 md:py-32 px-6 md:px-12 overflow-hidden border-b border-[#2A0A0A]"
    >
      {/* Faint side rune */}
      <div className="absolute -left-10 top-1/2 -translate-y-1/2 font-rune text-[18rem] text-[#8A0303] opacity-[0.04] select-none pointer-events-none">
        Qa'
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="bar-bronze" />
              <span className="font-mono-k text-[11px] uppercase tracking-[0.4em] text-[#C59B27]">
                ch.01&nbsp;//&nbsp;ja'chuq
              </span>
            </div>
            <h2
              data-testid="video-title"
              className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-[#EAEAEA]"
            >
              El mensaje<br />
              del <span className="text-[#FF2A2A]">comandante</span>
            </h2>
            <p className="mt-5 font-mono-k text-[#A0A0A0] text-sm md:text-base max-w-xl">
              Bicio enciende el canal. Habla del propósito de qapla.uk, de cómo se construyen
              las apps en este puente de mando — sin piedad, con honor.
            </p>
          </div>

          {/* Side status panel */}
          <div className="border border-[#2A0A0A] bg-black/60 p-4 font-mono-k text-[10px] uppercase tracking-[0.3em] text-[#A0A0A0] w-full md:w-72 panel-corner-tl panel-corner-tr relative">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#FF2A2A] flex items-center gap-2">
                <Radio size={12} className="animate-pulse" /> live feed
              </span>
              <span className="text-[#C59B27]">enc-A7</span>
            </div>
            <div className="flex justify-between"><span>ORIGEN</span><span className="text-[#EAEAEA]">QO'NOS</span></div>
            <div className="flex justify-between"><span>SEÑAL</span><span className="text-[#EAEAEA]">94%</span></div>
            <div className="flex justify-between"><span>SUBCH</span><span className="text-[#EAEAEA]">tlhIngan/ES</span></div>
          </div>
        </div>

        {/* Video Frame */}
        <div
          data-testid="video-frame"
          className="relative aspect-video w-full bg-black border-2 border-[#2A0A0A] shadow-[0_0_60px_rgba(138,3,3,0.25)] overflow-hidden group"
          style={{
            clipPath:
              "polygon(0 0, calc(100% - 40px) 0, 100% 40px, 100% 100%, 40px 100%, 0 calc(100% - 40px))",
          }}
        >
          {/* Corner brackets */}
          <div className="absolute top-3 left-3 w-6 h-6 border-l-2 border-t-2 border-[#C59B27] z-20" />
          <div className="absolute top-3 right-3 w-6 h-6 border-r-2 border-t-2 border-[#C59B27] z-20" />
          <div className="absolute bottom-3 left-3 w-6 h-6 border-l-2 border-b-2 border-[#C59B27] z-20" />
          <div className="absolute bottom-3 right-3 w-6 h-6 border-r-2 border-b-2 border-[#C59B27] z-20" />

          {/* Top HUD bar */}
          <div className="absolute top-0 left-0 right-0 h-8 z-10 flex items-center justify-between px-4 bg-gradient-to-b from-black/90 to-transparent font-mono-k text-[10px] uppercase tracking-[0.3em] text-[#A0A0A0]">
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#FF2A2A] animate-pulse" />
              REC · ch.01
            </span>
            <span>qapla.uk//transmisión</span>
            <span className="flex items-center gap-2">
              <Volume2 size={11} /> -12db
            </span>
          </div>

          {!playing ? (
            <>
              {/* Static / poster */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,#1a0303_0%,#000_70%)]">
                {/* Faint star bg */}
                <div className="absolute inset-0 opacity-30 mix-blend-screen"
                     style={{
                       backgroundImage:
                         "radial-gradient(1px 1px at 20% 30%, #fff, transparent 60%), radial-gradient(1px 1px at 70% 60%, #fff, transparent 60%), radial-gradient(2px 2px at 40% 80%, #fff, transparent 60%)",
                       backgroundSize: "400px 400px",
                     }}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <Sigil size={120} />
                  <div className="mt-6 font-rune text-[#C59B27] text-sm tracking-[0.4em] glitch-soft">
                    bIjatlh&nbsp;'e'&nbsp;yImev
                  </div>
                  <div className="mt-2 font-mono-k text-[#A0A0A0] text-xs uppercase tracking-[0.3em]">
                    pulsa para iniciar la transmisión
                  </div>
                </div>
              </div>

              {/* Play button */}
              <button
                data-testid="video-play-button"
                onClick={handlePlay}
                className="absolute inset-0 z-10 flex items-center justify-center group/play"
                aria-label="Play transmission"
              >
                <span className="relative flex items-center justify-center w-24 h-24 md:w-28 md:h-28 bg-[#8A0303]/30 border-2 border-[#C59B27] backdrop-blur-sm transition-all duration-300 group-hover/play:bg-[#8A0303] group-hover/play:scale-110"
                      style={{ clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }}
                >
                  <Play size={36} fill="#E6B836" stroke="#E6B836" className="ml-1" />
                  <span className="absolute inset-0 border-2 border-[#FF2A2A] animate-ping opacity-30"
                        style={{ clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)" }}
                  />
                </span>
              </button>
            </>
          ) : (
            <video
              ref={videoRef}
              data-testid="video-player"
              src={VIDEO_PLAYLIST[currentIndex]}
              controls
              autoPlay
              onEnded={handleEnded}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}

          {/* Bottom HUD bar */}
          <div className="absolute bottom-0 left-0 right-0 h-8 z-10 flex items-center justify-between px-4 bg-gradient-to-t from-black/90 to-transparent font-mono-k text-[10px] uppercase tracking-[0.3em] text-[#A0A0A0]">
            <span>cmdr·BICIO</span>
            <span>duration · ∞</span>
            <span className="text-[#E6B836]">qapla'</span>
          </div>

          {/* Scanline overlay inside the frame */}
          <div className="absolute inset-0 z-10 pointer-events-none"
               style={{ background: "repeating-linear-gradient(to bottom, transparent 0, transparent 2px, rgba(0,0,0,0.25) 3px, rgba(0,0,0,0.25) 4px)", mixBlendMode: "multiply" }}
          />
        </div>

        {/* Caption strip */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 font-mono-k text-[11px] uppercase tracking-[0.3em] text-[#A0A0A0]" data-testid="video-meta">
          <div><span className="text-[#C59B27]">// orador</span> · BICIO, capitán</div>
          <div><span className="text-[#C59B27]">// tema</span> · qapla.uk · manifiesto</div>
          <div><span className="text-[#C59B27]">// idioma</span> · tlhIngan&nbsp;Hol&nbsp;+&nbsp;español</div>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
